/**
 * Documentation-specific theme constants
 * Centralized color definitions for documentation components and pages
 */

export const docsColors = {
  // Text colors - use these for all text elements
  text: {
    // Headings - high contrast
    heading: "text-gray-900 dark:text-white",

    // Body text - medium contrast
    body: "text-gray-800 dark:text-gray-300",

    // Secondary/muted text - lower contrast
    muted: "text-gray-700 dark:text-gray-400",
    subtle: "text-gray-600 dark:text-gray-500",

    // Inside dark containers (like code blocks)
    onDark: "text-gray-900 dark:text-gray-300",
  },

  // Interactive elements
  link: {
    // Primary links
    primary: "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300",

    // Muted/secondary links
    muted: "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white",
  },

  // Icon colors
  icon: {
    // Primary icons
    primary: "text-blue-500 dark:text-blue-400",

    // Success/positive icons
    success: "text-green-500 dark:text-green-400",

    // Warning icons
    warning: "text-yellow-500 dark:text-yellow-400",

    // Error/danger icons
    error: "text-red-500 dark:text-red-400",

    // Muted icons
    muted: "text-gray-500 dark:text-gray-400",

    // Subtle icons
    subtle: "text-gray-400 dark:text-gray-500",
  },

  // Backgrounds
  bg: {
    // Page background
    page: "bg-white dark:bg-slate-900",

    // Card/section background
    card: "bg-gray-50 dark:bg-slate-800",

    // Code blocks
    code: "bg-gray-100 dark:bg-slate-800",
  },

  // Borders
  border: {
    // Default border
    default: "border-gray-200 dark:border-slate-700",

    // Muted border
    muted: "border-gray-100 dark:border-slate-800",
  },
} as const;

// Alert variant styles - for DocAlert component
export const alertVariants = {
  info: "bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/30 dark:border-blue-700/50 dark:text-blue-100",
  warning:
    "bg-yellow-50 border-yellow-200 text-yellow-900 dark:bg-yellow-900/30 dark:border-yellow-700/50 dark:text-yellow-100",
  success:
    "bg-green-50 border-green-200 text-green-900 dark:bg-green-900/30 dark:border-green-700/50 dark:text-green-100",
  note: "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/30 dark:border-amber-700/50 dark:text-amber-100",
} as const;

// Common component styles
export const docsComponents = {
  // Full page container
  pageContainer: `flex min-h-screen flex-col ${docsColors.bg.page}`,

  // Card style
  card: `${docsColors.bg.card} border ${docsColors.border.default} rounded-lg`,

  // Section heading
  sectionHeading: `text-xl font-semibold ${docsColors.text.heading} mb-2`,

  // Section description
  sectionDescription: `${docsColors.text.body} mb-4`,

  // Page description (for larger intro text)
  pageDescription: `text-xl ${docsColors.text.muted} mb-8`,
};

// Export type for alert variants
export type AlertVariant = keyof typeof alertVariants;
